import React from 'react';
import { motion } from 'framer-motion';
import { Plug, Sparkles, Search, FileSignature, CalendarCheck } from 'lucide-react';

const STEPS = [
  {
    day: 'Day 1',
    icon: Plug,
    title: 'Connect read-only',
    body: 'IAM role with read-only AWS scopes (CloudTrail, Config, IAM, Inspector, S3 inventory). 4 minutes via CloudFormation. No agents, no log shipping.',
    estimate: '4 min',
  },
  {
    day: 'Days 1–2',
    icon: Sparkles,
    title: 'Backfill 14 months',
    body: 'APEX replays existing CloudTrail and Config history. You inherit complete coverage from day one — including incidents that happened before APEX existed in your tenancy.',
    estimate: '~36 hrs',
  },
  {
    day: 'Day 3',
    icon: Search,
    title: 'First findings surface',
    body: 'Pattern Intelligence converges. Your first 3–5 governance gaps are visible with full evidence chains. Most customers find at least one previously-unknown exposure window in this window.',
    estimate: 'Day 3',
  },
  {
    day: 'Days 7–14',
    icon: FileSignature,
    title: 'First evidence packet',
    body: 'Sign and download your first audit-ready packet. Most teams start with SOC 2 CC7.1 or ISO 27001 A.12.4 and expand from there.',
    estimate: 'Day 14',
  },
  {
    day: 'Day 30',
    icon: CalendarCheck,
    title: 'Board-ready snapshot',
    body: 'Governance debt baseline established. Quarterly board snapshot is one click. Auditors get framework-mapped evidence on demand without a single follow-up email.',
    estimate: 'Day 30',
  },
];

export default function IntegrationsTimeline() {
  return (
    <div data-testid="integrations-timeline" className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h3 className="text-sm font-bold text-apex-ink">Your first 30 days with APEX</h3>
          <p className="text-[11px] text-apex-neutral mt-1 max-w-xl">From read-only connect to a board-ready snapshot — what you'll see and when.</p>
        </div>
        <span className="text-[10px] uppercase tracking-wider text-apex-lens font-semibold whitespace-nowrap">Onboarding</span>
      </div>

      <ol className="relative border-l border-dashed border-slate-300 ml-3 space-y-5">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          return (
            <motion.li
              key={s.day}
              initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.07 }}
              data-testid={`onboarding-step-${i + 1}`}
              className="ml-5"
            >
              <span className="absolute -left-[11px] flex w-5 h-5 items-center justify-center rounded-full bg-apex-lens text-white shadow ring-4 ring-white">
                <Icon className="w-2.5 h-2.5" />
              </span>
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="text-[10px] uppercase tracking-wider text-apex-neutral font-mono">{s.day}</span>
                <h4 className="text-sm font-semibold text-apex-ink">{s.title}</h4>
                <span className="ml-auto text-[10px] font-mono text-apex-lens">{s.estimate}</span>
              </div>
              <p className="text-[12px] text-apex-ink-2 leading-relaxed mt-1">{s.body}</p>
            </motion.li>
          );
        })}
      </ol>
    </div>
  );
}
